/**
 * Pure SVG Code128 barcode generator
 *
 * ทำงานบน Cloudflare Workers ได้ (ไม่ใช้ Canvas / Node.js APIs)
 * รองรับ Code128B — ครอบคลุม ASCII 32-127 (พอสำหรับ Invoice numbers, Document IDs)
 *
 * อ้างอิง: https://en.wikipedia.org/wiki/Code_128
 */

// ─── Code128B character encodings ─────────────────────────────────────────────
// แต่ละ character encode เป็น 11-bit pattern (1=bar, 0=space)
const CODE128B_PATTERNS: Record<number, string> = {
  32:  "11011001100", // SPACE
  33:  "11001101100", // !
  34:  "11001100110", // "
  35:  "10010011000", // #
  36:  "10010001100", // $
  37:  "10001001100", // %
  38:  "10011001000", // &
  39:  "10011000100", // '
  40:  "10001100100", // (
  41:  "11001001000", // )
  42:  "11001000100", // *
  43:  "11000100100", // +
  44:  "10110011100", // ,
  45:  "10011011100", // -
  46:  "10011001110", // .
  47:  "10111001100", // /
  48:  "10011101100", // 0
  49:  "10011100110", // 1
  50:  "11001110010", // 2
  51:  "11001011100", // 3
  52:  "11001001110", // 4
  53:  "11011100100", // 5
  54:  "11001110100", // 6
  55:  "11101101110", // 7
  56:  "11101001100", // 8
  57:  "11100101100", // 9
  58:  "11100100110", // :
  59:  "11101100100", // ;
  60:  "11100110100", // <
  61:  "11100110010", // =
  62:  "11011011000", // >
  63:  "11011000110", // ?
  64:  "11000110110", // @
  65:  "10100011000", // A
  66:  "10001011000", // B
  67:  "10001000110", // C
  68:  "10110001000", // D
  69:  "10001101000", // E
  70:  "10001100010", // F
  71:  "11010001000", // G
  72:  "11000101000", // H
  73:  "11000100010", // I
  74:  "10110111000", // J
  75:  "10110001110", // K
  76:  "10001101110", // L
  77:  "10111011000", // M
  78:  "10111000110", // N
  79:  "10001110110", // O
  80:  "11101110110", // P
  81:  "11010001110", // Q
  82:  "11000101110", // R
  83:  "11011101000", // S
  84:  "11011100010", // T
  85:  "11011101110", // U
  86:  "11101011000", // V
  87:  "11101000110", // W
  88:  "11100010110", // X
  89:  "11101101000", // Y
  90:  "11101100010", // Z
  91:  "11100011010", // [
  92:  "11101111010", // \
  93:  "11001000010", // ]
  94:  "11110001010", // ^
  95:  "10100110000", // _
  96:  "10100001100", // `
  97:  "10010110000", // a
  98:  "10010000110", // b
  99:  "10000101100", // c
  100: "10000100110", // d
  101: "10110010000", // e
  102: "10110000100", // f
  103: "10011010000", // g
  104: "10011000010", // h
  105: "10000110100", // i
  106: "10000110010", // j
  107: "11000010010", // k
  108: "11001010000", // l
  109: "11110111010", // m
  110: "11000010100", // n
  111: "10001111010", // o
  112: "10100111100", // p
  113: "10010111100", // q
  114: "10010011110", // r
  115: "10111100100", // s
  116: "10011110100", // t
  117: "10011110010", // u
  118: "11110100100", // v
  119: "11110010100", // w
  120: "11110010010", // x
  121: "11011011110", // y
  122: "11011110110", // z
  123: "11110110110", // {
  124: "10101111000", // |
  125: "10100011110", // }
  126: "10001011110", // ~
};

// Special symbol values (ไม่ใช่ ASCII — ใช้ index ใน Code128 table)
// START B = value 104, STOP = value 106
const START_B = "11010010000";
const STOP    = "11000111010";
// STOP termination bar
const STOP_TERM = "11";

// Code128B symbol values (ใช้คำนวณ checksum)
// value = charCode - 32
function charValue(charCode: number): number {
  return charCode - 32;
}

// Special checksum patterns for Code128B values 95-102
// Module-level constant — สร้างครั้งเดียว ไม่ต้องสร้างซ้ำทุก call
const SPECIAL_PATTERNS: Record<number, string> = {
  95:  "11110101000",
  96:  "11110100010",
  97:  "10111100010",
  98:  "10111110100",
  99:  "10111110010",
  100: "11110101110",
  101: "11110111100",
  102: "11010000100",
  // value 103 = START A (ไม่ใช้เป็น checksum)
};

// ─── Security helpers ────────────────────────────────────────────────────────

/**
 * Validate color value ให้เป็น safe CSS color (ป้องกัน SVG attribute injection)
 * อนุญาต: named colors, #hex, rgb(), rgba(), hsl(), hsla()
 * ห้าม: ", ', <, >, ; ที่อาจ break out of SVG attribute
 */
function isSafeColor(color: string): boolean {
  return /^[a-zA-Z0-9#(),%./ -]+$/.test(color);
}

/**
 * Encode text to Code128B SVG string
 *
 * @param text    - ข้อความที่จะ encode (ASCII 32-126)
 * @param height  - ความสูงของแถบ barcode (px) default 50
 * @param color   - สีของแถบ default "#000000"
 */
export function generateBarcodeSVG(
  text: string,
  height = 50,
  color = "#000000",
): string {
  // Validate text length (ป้องกัน DoS จาก input ยาวมาก)
  if (text.length > 128) {
    throw new Error(`barcode: text too long (max 128 characters, got ${text.length})`);
  }

  // Validate color ป้องกัน SVG attribute injection
  if (!isSafeColor(color)) {
    throw new Error(`barcode: unsafe color value "${color}"`);
  }

  // Validate: ต้องเป็น ASCII 32-126 เท่านั้น
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 32 || code > 126) {
      throw new Error(`barcode: character '${text[i]}' (code ${code}) is not supported in Code128B`);
    }
  }

  // ─── Build binary pattern ────────────────────────────────────────────────────

  // 1. Start B symbol (value = 104)
  const parts: string[] = [START_B];
  let checksum = 104;

  // 2. Data characters
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const pattern = CODE128B_PATTERNS[code];
    if (!pattern) {
      throw new Error(`barcode: no pattern for char code ${code}`);
    }
    parts.push(pattern);
    checksum += charValue(code) * (i + 1);
  }

  // 3. Checksum symbol (value mod 103)
  const checksumValue = checksum % 103;
  // Code128B values 0-94 map to ASCII 32-126; 95-102 use SPECIAL_PATTERNS (module-level)

  let checksumPattern: string;
  if (checksumValue <= 94) {
    const checksumCharCode = checksumValue + 32;
    checksumPattern = CODE128B_PATTERNS[checksumCharCode];
    if (!checksumPattern) {
      throw new Error(`barcode: no pattern for checksum value ${checksumValue}`);
    }
  } else {
    checksumPattern = SPECIAL_PATTERNS[checksumValue];
    if (!checksumPattern) {
      throw new Error(`barcode: no special pattern for checksum value ${checksumValue}`);
    }
  }
  parts.push(checksumPattern);

  // 4. Stop symbol + termination bar
  parts.push(STOP, STOP_TERM);

  const binary = parts.join("");

  // ─── Render SVG ──────────────────────────────────────────────────────────────

  const barWidth = 2; // px per module
  const quietZone = 10; // quiet zone px each side
  const totalWidth = binary.length * barWidth + quietZone * 2;
  const totalHeight = height;

  const rects: string[] = [];
  let x = quietZone;
  let i = 0;
  while (i < binary.length) {
    const bit = binary[i];
    // ขยายต่อเนื่องเพื่อรวม run ของ bit เดียวกัน
    let runLen = 1;
    while (i + runLen < binary.length && binary[i + runLen] === bit) {
      runLen++;
    }
    const w = runLen * barWidth;
    if (bit === "1") {
      rects.push(`<rect x="${x}" y="0" width="${w}" height="${height}" fill="${color}"/>`);
    }
    x += w;
    i += runLen;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}" shape-rendering="crispEdges">${rects.join("")}</svg>`;
}

/**
 * Encode barcode as Base64 Data URI (สำหรับใช้ใน <img src="...">)
 * ใช้ btoa() ซึ่งมีใน Cloudflare Workers global scope
 */
export function generateBarcodeDataURI(
  text: string,
  height = 50,
  color = "#000000",
): string {
  const svg = generateBarcodeSVG(text, height, color);
  // btoa ต้องการ ASCII — SVG ที่ generate ไม่มี non-ASCII
  const b64 = btoa(svg);
  return `data:image/svg+xml;base64,${b64}`;
}
